import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Alert,
    CircularProgress,
    Typography,
    IconButton,
    Divider,
} from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import useNotification from "../common/Notification";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const EditSubUserModal = ({ open, onClose, user, onUserUpdated }) => {
    const [formData, setFormData] = useState({
        password: "",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const notify = useNotification();

    useEffect(() => {
        if (user) {
            setFormData({
                password: "", // Don't pre-fill password for security
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError("");
    };

    const validateForm = () => {
        // Password is required for update
        if (!formData.password.trim()) {
            setError("Password is required");
            return false;
        }
        if (formData.password.length < 6) {
            setError("Password must be at least 6 characters long");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        if (!user || !user.id) {
            setError("User information is missing");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await axiosInstance.put(
                `${base_url}/whatsapp-business/users/${user.id}/password`,
                {
                    password: formData.password,
                }
            );

            if (response.data && response.data.status === "success") {
                notify("Password updated successfully", "success");
                handleClose();
                if (onUserUpdated) onUserUpdated();
            } else {
                throw new Error(response.data?.message || "Failed to update password");
            }
        } catch (err) {
            console.error("Error updating password:", err);
            let errorMessage = "Failed to update password";
            
            if (err.response?.data) {
                if (err.response.data.errors) {
                    // Handle validation errors
                    const errors = err.response.data.errors;
                    errorMessage = Object.values(errors).flat().join(", ");
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                } else if (err.response.data.error) {
                    errorMessage = err.response.data.error;
                }
            }
            
            setError(errorMessage);
            notify(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            password: "",
        });
        setError("");
        setLoading(false);
        onClose();
    };

    if (!user) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${WB_UI.color.border}` } }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2,
                    px: 2.5,
                    pb: 1.5,
                }}
            >
                <Typography sx={{ fontWeight: 700, fontSize: "1rem", letterSpacing: "-0.01em" }}>Update password</Typography>
                <IconButton onClick={handleClose} size="small" aria-label="Close" sx={{ color: "text.secondary" }}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />

            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ pt: 2.5 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 0.5 }}>
                        {user ? (
                            <TextField
                                fullWidth
                                label="Email"
                                value={user.email || ""}
                                disabled
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: WB_UI.radius.card } }}
                            />
                        ) : null}
                        <TextField
                            fullWidth
                            label="New password *"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            placeholder="Min 6 characters"
                            disabled={loading}
                            helperText="At least 6 characters"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: WB_UI.radius.card } }}
                        />
                    </Box>
                </DialogContent>

                <Divider />
                <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
                    <Button onClick={handleClose} disabled={loading} sx={WB_SX.secondaryButton}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={WB_SX.primaryButton}
                        startIcon={loading ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : <Save sx={{ fontSize: 18 }} />}
                    >
                        {loading ? "Updating…" : "Update password"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default EditSubUserModal;

